import React, { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, ContentCard, FilterSetController, DropdownItem, Icon, Table } from '@weezevent/nacre';
import TicketOfficeEventRow from './ticketOfficeEventRow';
import SortDropdown from '../../../../components/sortDropdown';
import { useSort, useFilters, ManagerContext } from '../../../manager/components/context';

const spec = {
    multiple: [],
    fields: ['search'],
    aliases: {}
};

export default function TicketOfficeOperatorList({ ticket_office, organization, setDeleteObject, syncTicketOffice }) {
    const history = useHistory();
    const { t } = useTranslation();
    const [filters, setFilters] = useFilters(spec);
    const [ordering, setOrdering] = useSort('name');
    const { setContext } = React.useContext(ManagerContext);

    const operators = useMemo(() => {
        if (!ticket_office.operators || !ticket_office.operators.length) {
            return [];
        }
        // spread cause we dont want to change the original object
        return [...ticket_office.operators].sort((a, b) => {
            const isDescending = ordering.startsWith('-');
            // remove '-' if ordering starts with it
            const field = isDescending ? ordering.slice(1) : ordering;

            if (isDescending) {
                return b[field].localeCompare(a[field]);
            }
            return a[field].localeCompare(b[field]);
        });
    }, [ticket_office, ticket_office.operators, ordering]);

    const onChangeSort = ordering => {
        setOrdering(ordering);
    };

    const handleSearch = React.useCallback(
        value => {
            let _filters = { ...filters };
            if (!value || value === '') {
                delete _filters['search'];
                return setFilters(_filters);
            }
            _filters['search'] = value;
            return setFilters(_filters);
        },
        [filters]
    );

    const handleFilters = React.useCallback(
        values => {
            if (filters.search !== undefined && filters.search.length > 0) {
                values.search = filters.search;
            } else {
                delete values.search;
            }
            setFilters(values);
        },
        [filters]
    );

    const filteredOperators = React.useMemo(() => {
        if (!operators.length) return [];

        const { search } = filters;
        if (search) {
            const searchTerm = search.toLowerCase();
            return operators.filter(operator => operator.name.toLowerCase().includes(searchTerm) || operator.email.toLowerCase().includes(searchTerm));
        }

        return operators;
    }, [filters, operators]);

    // The ManagerContext is shared across all ticket office pages and needs to be reset when the component mounts
    React.useEffect(() => {
        setContext({});
    }, []);

    return (
        <Card>
            <ContentCard>
                <FilterSetController
                    labelTotal={t('ticket.ticket_offices.operators.table.operator', { count: filteredOperators.length })}
                    onChange={values => handleFilters(values)}
                    search={{
                        onChange: (synth, value) => {
                            handleSearch(value);
                        },
                        handleReset: () => {
                            handleSearch('');
                        },
                        placeholder: t('ticket.common.action.search'),
                        value: filters?.search || ''
                    }}
                    total={operators && filteredOperators.length}
                />
            </ContentCard>
            <Table useNew>
                <Table.THead>
                    <Table.Tr>
                        <SortDropdown
                            name="name"
                            ordering={ordering}
                            label={t('ticket.common.name')}
                            items={[
                                <DropdownItem.Icons key={1} item={t('ticket.common.ordering.A-Z')} checked={ordering === 'name'} onClick={() => onChangeSort('name')} />,
                                <DropdownItem.Icons key={2} item={t('ticket.common.ordering.Z-A')} checked={ordering === '-name'} onClick={() => onChangeSort('-name')} />
                            ]}
                        />
                        <SortDropdown
                            name="email"
                            ordering={ordering}
                            label={t('ticket.ticket_offices.operators.table.email')}
                            items={[
                                <DropdownItem.Icons key={1} item={t('ticket.common.ordering.A-Z')} checked={ordering === 'email'} onClick={() => onChangeSort('email')} />,
                                <DropdownItem.Icons key={2} item={t('ticket.common.ordering.Z-A')} checked={ordering === '-email'} onClick={() => onChangeSort('-email')} />
                            ]}
                        />
                        <th>{t('ticket.ticket_offices.operators.table.password')}</th>
                        <th>{t('ticket.common.status')}</th>
                        <th>{t('ticket.common.actions')}</th>
                    </Table.Tr>
                </Table.THead>
                <Table.TBody>
                    {filteredOperators.map((operator, index) => (
                        <TicketOfficeEventRow
                            key={index}
                            ticket_office={ticket_office}
                            ticket_office_operator={operator}
                            organization={organization}
                            onDelete={() => setDeleteObject(operator)}
                            syncTicketOffice={syncTicketOffice}
                        />
                    ))}
                </Table.TBody>
            </Table>
            {!filteredOperators.length && (
                <Card.EmptyState
                    icon={<Icon name="list" />}
                    title={t('ticket.ticket_offices.operators.operators_empty.title')}
                    subtitle={t('ticket.ticket_offices.operators.operators_empty.subtitle')}
                    labelButton={t('ticket.ticket_offices.operators.operators_empty.label')}
                    onClick={() => history.push(`/ticket/O${organization.id}/ticket_offices/${ticket_office.id}/users/new`)}
                />
            )}
        </Card>
    );
}
